import { useState, useEffect, useCallback, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Sparkles, Upload, Download, ArrowRight, Check, ChevronLeft, Share2, Camera, X, AlertTriangle, Lock } from "lucide-react";
import axios from "axios";
import { API, STYLES, pageVariants } from "../components/app/constants";
import { CreditUpsellModal, PremiumLoader, SocialSharePanel, BeforeAfterSlider, NoiseOverlay } from "../components/shared";
import { SEO } from "../components/shared/SEO";
import { ShareReferralBox } from "./ShareReferralBox";

export const CreatePage = ({ user, onUserUpdate, onNavigate }) => {
  const [selectedStyle, setSelectedStyle] = useState(null);
  const [uploadedImage, setUploadedImage] = useState(null);
  const [uploadedBase64, setUploadedBase64] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [result, setResult] = useState(null);
  const [multiResults, setMultiResults] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const [showComparison, setShowComparison] = useState(false);
  const [showUpsell, setShowUpsell] = useState(false);
  const [hdDownloading, setHdDownloading] = useState(false);
  const [stylesUnlocked, setStylesUnlocked] = useState(false);
  const [showGuidelines, setShowGuidelines] = useState(false);
  const [faceValidation, setFaceValidation] = useState(null);
  const [validatingFace, setValidatingFace] = useState(false);
  const [enhancedMode, setEnhancedMode] = useState(false);
  const [additionalImages, setAdditionalImages] = useState([]);
  const [hasUltimate, setHasUltimate] = useState(false);

  // Check share-to-unlock status and Ultimate pack ownership
  useEffect(() => {
    if (user) {
      axios.get(`${API}/share/unlock-status/${user.id}`).then(res => {
        setStylesUnlocked(res.data.styles_unlocked);
      }).catch(() => {});
      // Check if user has Ultimate pack
      axios.get(`${API}/promotions/${user.id}`).then(res => {
        setHasUltimate(!res.data.first_purchase_discount);
      }).catch(() => {});
    }
  }, [user]);

  // Compress image to max 1200px and reasonable quality for upload
  const compressImage = useCallback((dataUrl) => {
    return new Promise((resolve) => {
      const img = new window.Image();
      img.onload = () => {
        const canvas = document.createElement("canvas");
        const MAX_DIM = 1200;
        let { width, height } = img;
        if (width > MAX_DIM || height > MAX_DIM) {
          if (width > height) {
            height = Math.round((height * MAX_DIM) / width);
            width = MAX_DIM;
          } else {
            width = Math.round((width * MAX_DIM) / height);
            height = MAX_DIM;
          }
        }
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0, width, height);
        const compressed = canvas.toDataURL("image/jpeg", 0.85);
        resolve(compressed);
      };
      img.src = dataUrl;
    });
  }, []);

  const validateFace = useCallback(async (base64) => {
    setValidatingFace(true);
    setFaceValidation(null);
    try {
      const res = await axios.post(`${API}/validate-face`, { image_base64: base64 });
      setFaceValidation(res.data);
      if (!res.data.valid && !showGuidelines) {
        setShowGuidelines(true);
      }
    } catch {
      // Face validation is non-critical
    }
    setValidatingFace(false);
  }, [showGuidelines]);

  const handleFileSelect = useCallback((file) => {
    if (!file || !file.type.startsWith("image/")) return;
    
    const reader = new FileReader();
    reader.onload = async (e) => {
      const rawDataUrl = e.target.result;
      // Compress for display and upload
      const compressed = await compressImage(rawDataUrl);
      setUploadedImage(compressed);
      const base64 = compressed.split(",")[1];
      setUploadedBase64(base64);
      validateFace(base64);
    };
    reader.readAsDataURL(file);
  }, [validateFace, compressImage]);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files[0];
    handleFileSelect(file);
  }, [handleFileSelect]);

  const handleGenerate = async () => {
    if (!selectedStyle || !uploadedBase64) return;
    
    const requiredCredits = selectedStyle === "all" ? 7 : 1;
    if (user.credits < requiredCredits) {
      setShowUpsell(true);
      return;
    }
    
    setGenerating(true);
    setResult(null);
    setMultiResults(null);
    
    try {
      if (selectedStyle === "all") {
        // Generate all styles with polling
        const startResponse = await axios.post(`${API}/generate-all-styles`, {
          user_id: user.id,
          image_base64: uploadedBase64
        });
        
        const jobId = startResponse.data.job_id;
        
        // Poll for results
        const pollForResults = async () => {
          const maxAttempts = 180; // 6 minutes max for multi-style (2s intervals)          let attempts = 0;
          
          while (attempts < maxAttempts) {
            await new Promise(resolve => setTimeout(resolve, 2000)); // Wait 2 seconds
            
            try {
              const statusResponse = await axios.get(`${API}/generate-all-styles/status/${jobId}`);
              const status = statusResponse.data;
              
              // Update results as they come in (show progress)
              if (status.portraits && status.portraits.length > 0) {
                setMultiResults(status.portraits);
              }
              
              if (status.status === "completed") {
                setMultiResults(status.portraits);
                onUserUpdate({ ...user, credits: status.credits_remaining });
                return;
              }
              
              if (status.status === "error") {
                throw new Error(status.error || "Generation failed");
              }
            } catch (pollErr) {
              if (pollErr.response?.status === 404) {
                throw new Error("Job not found");
              }
              // Continue polling on other errors
            }
            
            attempts++;
          }
          
          throw new Error("Generation timed out");
        };
        
        await pollForResults();
      } else {
        // Generate single style (async with polling)
        const endpoint = enhancedMode && additionalImages.length > 0 ? `${API}/generate-enhanced` : `${API}/generate`;
        const payload = {
          user_id: user.id,
          style: selectedStyle,
          image_base64: uploadedBase64
        };
        if (enhancedMode && additionalImages.length > 0) {
          payload.additional_images = additionalImages.filter(img => img?.base64).map(img => img.base64);
        }
        
        const startResponse = await axios.post(endpoint, payload);
        
        const jobId = startResponse.data.job_id;
        
        // Poll for single generation result
        const maxAttempts = 150; // 5 minutes max for single style
        let attempts = 0;
        let consecutiveNetworkErrors = 0;
        
        while (attempts < maxAttempts) {
          await new Promise(resolve => setTimeout(resolve, 2000));
          
          try {
            const statusResponse = await axios.get(`${API}/generate/status/${jobId}`);
            const status = statusResponse.data;
            consecutiveNetworkErrors = 0; // Reset on successful poll
            
            if (status.status === "completed") {
              setResult(status);
              onUserUpdate({ ...user, credits: status.credits_remaining });
              break;
            }
            
            if (status.status === "error") {
              throw new Error(status.error || "Generation failed");
            }
          } catch (pollErr) {
            // Genuine backend errors abort
            if (pollErr.response?.status === 404) {
              throw new Error("Job not found");
            }
            if (pollErr.message === "Generation failed" || pollErr.message?.includes("Budget")) {
              throw pollErr;
            }
            // Otherwise it's a transient network error — keep polling.
            // Only abort if we have 8+ consecutive network failures (~16s of dead connection).
            consecutiveNetworkErrors += 1;
            if (consecutiveNetworkErrors > 8) {
              throw new Error("Connection lost. The portrait may still be generating — check your gallery in a moment.");
            }
          }
          
          attempts++;
        }
        
        if (attempts >= maxAttempts) {
          throw new Error("Generation timed out. Please try again.");
        }
      }
    } catch (err) {
      if (err.response?.status === 402) {
        setShowUpsell(true);
      } else {
        const msg = err.message || "Generation failed. Please try again.";
        if (msg.includes("Budget has been exceeded") || msg.includes("budget")) {
          alert("AI service temporarily unavailable. Please try again in a few minutes.");
        } else {
          alert(msg);
        }
      }
    } finally {
      setGenerating(false);
    }
  };

  const downloadImage = async (portraitId) => {
    if (!portraitId && !result?.portrait_id) return;
    const pid = portraitId || result.portrait_id;
    
    setHdDownloading(true);
    try {
      const res = await axios.get(`${API}/portraits/download-hd/${pid}?user_id=${user.id}`);
      const link = document.createElement("a");
      link.href = `data:image/png;base64,${res.data.image_base64}`;
      link.download = `starframe-${selectedStyle}-${Date.now()}.png`;
      link.click();
      if (res.data.credits_remaining !== undefined) {
        onUserUpdate({ ...user, credits: res.data.credits_remaining });
      }
    } catch (err) {
      if (err.response?.status === 402) {
        setShowUpsell(true);
      } else {
        alert("Download failed. Please try again.");
      }
    }
    setHdDownloading(false);
  };

  const resetCreate = () => {
    setSelectedStyle(null);
    setUploadedImage(null);
    setUploadedBase64(null);
    setResult(null);
    setMultiResults(null);
  };

  // Show prompt to sign in if no user
  if (!user) {
    return (
      <motion.div 
        variants={pageVariants}
        initial="initial"
        animate="animate"
        exit="exit"
        className="min-h-screen pt-32 pb-20 px-6 flex items-center justify-center"
      >
        <div className="text-center">
          <p className="text-white/60 mb-4">Please sign in to create portraits</p>
          <button 
            onClick={() => onNavigate("/start")}
            className="btn-primary"
            data-testid="create-signin-btn"
          >
            Get Started
          </button>
        </div>
      </motion.div>
    );
  }

  return (
    <motion.div 
      variants={pageVariants}
      initial="initial"
      animate="animate"
      exit="exit"
      className="min-h-screen pt-28 pb-20 px-6"
    >
      <SEO title="Create AI Portrait" path="/create" description="Upload your photo and choose from 12 stunning AI portrait styles. LinkedIn headshot, magazine cover, red carpet, fantasy, anime & more. Face-preserving AI technology. Results in seconds." />
      <div className="max-w-6xl mx-auto">
        {/* Multi-Style Results View */}
        {multiResults && multiResults.length > 0 ? (
          <div className="text-center">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="mb-8"
            >
              <p className="text-xs uppercase tracking-[0.3em] text-[#D4AF37] mb-4">All Styles Ready!</p>
              <h1 className="font-serif text-4xl md:text-5xl tracking-tight">Your Collection</h1>
            </motion.div>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4 mb-8">
              {multiResults.map((portrait, index) => {
                const isLocked = !stylesUnlocked && index >= 3;
                return (
                <motion.div
                  key={portrait.portrait_id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className="text-center"
                >
                  <div className="portrait-result glass-card p-2 mb-2 relative overflow-hidden">
                    <img 
                      src={`data:image/png;base64,${portrait.image_base64}`} 
                      alt={`${portrait.style} portrait`}
                      className={isLocked ? "blur-lg" : ""}
                    />
                    {isLocked && (
                      <div className="absolute inset-0 flex flex-col items-center justify-center bg-black/40 backdrop-blur-sm">
                        <Lock className="w-6 h-6 text-[#D4AF37] mb-2" />
                        <p className="text-xs text-white/80 font-medium">Share to Unlock</p>
                      </div>
                    )}
                  </div>
                  <p className="text-sm text-white/60 capitalize mb-2">{portrait.style.replace("_", " ")}</p>
                  {isLocked ? (
                    <button 
                      onClick={async () => {
                        const shareText = "Just created an amazing AI portrait with StarFrame AI!";
                        const shareUrl = "https://starframeai.com/start";
                        const url = `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(shareUrl)}`;
                        window.open(url, "_blank", "width=600,height=400");
                        try {
                          await axios.post(`${API}/share/unlock-styles`, { platform: "twitter" });
                          setStylesUnlocked(true);
                        } catch { /* non-critical */ }
                      }}
                      className="text-xs text-[#D4AF37] hover:underline flex items-center justify-center gap-1"
                      data-testid={`unlock-style-${index}`}
                    >
                      <Share2 className="w-3 h-3" /> Share to Unlock
                    </button>
                  ) : (
                    <button 
                      onClick={() => {
                        const link = document.createElement("a");
                        link.href = `data:image/png;base64,${portrait.image_base64}`;
                        link.download = `starframe-${portrait.style}-${Date.now()}.png`;
                        link.click();
                      }}
                      className="text-xs text-[#D4AF37] hover:underline"
                    >
                      Download
                    </button>
                  )}
                </motion.div>
                );
              })}
            </div>
            
            <button 
              onClick={resetCreate}
              className="btn-primary"
              data-testid="create-another-button"
            >
              Create More
            </button>
          </div>
        ) : result ? (
          <div className="text-center">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="mb-8"
            >
              <p className="text-xs uppercase tracking-[0.3em] text-[#D4AF37] mb-4">Your Portrait is Ready</p>
              <h1 className="font-serif text-4xl md:text-5xl tracking-tight">Stunning.</h1>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="max-w-lg mx-auto mb-8"
            >
              {/* Before/After Comparison Toggle */}
              {uploadedImage && (
                <div className="flex justify-center mb-4">
                  <div className="flex bg-white/10 rounded-full p-1">
                    <button
                      onClick={() => setShowComparison && setShowComparison(false)}
                      className={`px-4 py-1.5 rounded-full text-sm transition-colors ${!showComparison ? 'bg-[#D4AF37] text-black' : 'text-white/60 hover:text-white'}`}
                    >
                      Result Only
                    </button>
                    <button
                      onClick={() => setShowComparison && setShowComparison(true)}
                      className={`px-4 py-1.5 rounded-full text-sm transition-colors ${showComparison ? 'bg-[#D4AF37] text-black' : 'text-white/60 hover:text-white'}`}
                    >
                      Before/After
                    </button>
                  </div>
                </div>
              )}
              
              {showComparison && uploadedImage ? (
                <BeforeAfterSlider
                  beforeImage={uploadedImage}
                  afterImage={`data:image/png;base64,${result.image_base64}`}
                  beforeLabel="Original"
                  afterLabel={STYLES.find(s => s.id === result.style)?.name || "Generated"}
                />
              ) : (
                <div className="portrait-result glass-card p-2">
                  <img 
                    src={`data:image/png;base64,${result.image_base64}`} 
                    alt="Generated portrait"
                    data-testid="generated-portrait"
                  />
                </div>
              )}
            </motion.div>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button 
                onClick={() => downloadImage(result.portrait_id)}
                disabled={hdDownloading}
                className="btn-primary flex items-center justify-center gap-2"
                data-testid="download-button"
              >
                {hdDownloading ? (
                  <><div className="w-4 h-4 border-2 border-black/30 border-t-black rounded-full animate-spin" /> Downloading...</>
                ) : (
                  <><Download className="w-4 h-4" /> Download HD (1 Credit)</>
                )}
              </button>
              <button 
                onClick={resetCreate}
                className="btn-secondary"
                data-testid="create-another-button"
              >
                Create Another
              </button>
            </div>
            
            {/* Social Share Panel */}
            {result.portrait_id && (
              <div className="max-w-md mx-auto mt-6">
                <SocialSharePanel 
                  portraitId={result.portrait_id}
                  onShareBonus={async (platform) => {
                    try {
                      const res = await axios.post(`${API}/share/first-time`, { user_id: user.id, platform });
                      if (res.data.credits_awarded) {
                        onUserUpdate({ ...user, credits: user.credits + res.data.credits_awarded });
                      }
                    } catch { /* non-critical */ }
                  }}
                />
              </div>
            )}
            
            {/* Referral & Share Section */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="mt-12 max-w-md mx-auto"
            >
              <ShareReferralBox user={user} onUserUpdate={onUserUpdate} />
            </motion.div>
          </div>
        ) : (
          <>
            {/* Header */}
            <div className="text-center mb-12">
              <p className="text-xs uppercase tracking-[0.3em] text-[#D4AF37] mb-4">Create Portrait</p>
              <h1 className="font-serif text-4xl md:text-5xl tracking-tight">
                {!selectedStyle ? "Choose Your Style" : !uploadedImage ? "Upload Your Photo" : "Ready to Generate"}
              </h1>
            </div>

            {/* Step 1: Style Selection */}
            {!selectedStyle && (
              <>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
                  {STYLES.map((style, index) => (
                    <motion.div
                      key={style.id}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: Math.min(index * 0.05, 0.5) }}
                      onClick={() => setSelectedStyle(style.id)}
                      className="style-card cursor-pointer"
                      data-testid={`select-style-${style.id}`}
                    >
                      <img src={style.image} alt={style.name} loading="lazy" />
                      <div className="absolute bottom-0 left-0 right-0 p-6 z-10">
                        <h3 className="font-serif text-xl mb-1">{style.name}</h3>
                        <p className="text-xs text-white/60">{style.description}</p>
                      </div>
                    </motion.div>
                  ))}
                </div>
                
                {/* Multi-Style Pack Option */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.5 }}
                  className="mt-8 text-center"
                >
                  <div className="inline-block p-6 glass-card border border-[#D4AF37]/30">
                    <p className="text-xs uppercase tracking-[0.2em] text-[#D4AF37] mb-2">Special Offer</p>
                    <h3 className="font-serif text-2xl mb-2">Get All 12 Styles</h3>
                    <p className="text-white/60 text-sm mb-4">Best deal! Only 7 credits for all 12 styles</p>
                    <button 
                      onClick={() => setSelectedStyle("all")}
                      className="btn-primary"
                      data-testid="select-all-styles"
                    >
                      Generate All Styles
                    </button>
                  </div>
                </motion.div>
              </>
            )}

            {/* Step 2: Upload */}
            {selectedStyle && !uploadedImage && (
              <div className="max-w-xl mx-auto">
                <button 
                  onClick={() => setSelectedStyle(null)}
                  className="flex items-center gap-2 text-white/60 hover:text-white mb-8 transition-colors"
                  data-testid="back-to-styles"
                >
                  <ChevronLeft className="w-4 h-4" />
                  Change Style
                </button>
                
                {/* Upload Zone */}
                <div 
                  className={`upload-zone p-8 sm:p-12 text-center cursor-pointer ${dragOver ? 'dragover' : ''}`}
                  onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                  onDragLeave={() => setDragOver(false)}
                  onDrop={handleDrop}
                  onClick={() => document.getElementById("file-input").click()}
                  data-testid="upload-zone"
                >
                  <Upload className="w-12 h-12 text-[#D4AF37] mx-auto mb-4" />
                  <h3 className="font-serif text-2xl mb-2">Drop Your Photo</h3>
                  <p className="text-white/60 mb-2">or click to browse</p>
                  <p className="text-xs text-white/40 mb-3">Supports JPG, PNG, WebP</p>
                  <p className="text-xs text-white/30 max-w-sm mx-auto leading-relaxed">Use a clear, front-facing photo with good lighting for best results</p>
                </div>
                
                {/* Divider */}
                <div className="flex items-center gap-4 my-6">
                  <div className="flex-1 h-px bg-white/10" />
                  <span className="text-white/40 text-sm uppercase tracking-widest">or</span>
                  <div className="flex-1 h-px bg-white/10" />
                </div>
                
                {/* Camera Capture Button */}
                <button
                  onClick={() => document.getElementById("camera-input").click()}
                  className="w-full btn-secondary flex items-center justify-center gap-3 py-4"
                  data-testid="camera-capture-btn"
                >
                  <Camera className="w-5 h-5 text-[#D4AF37]" />
                  <span>Take a Selfie</span>
                </button>
                
                <input
                  id="file-input"
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(e) => handleFileSelect(e.target.files[0])}
                  data-testid="file-input"
                />
                
                <input
                  id="camera-input"
                  type="file"
                  accept="image/*"
                  capture="user"
                  className="hidden"
                  onChange={(e) => handleFileSelect(e.target.files[0])}
                  data-testid="camera-input"
                />
              </div>
            )}
            
            {/* Face Validation Feedback */}
            {uploadedBase64 && (validatingFace || faceValidation) && (
              <div className="mt-4">
                {validatingFace && (
                  <div className="flex items-center gap-2 text-white/50 text-sm">
                    <div className="spinner" style={{ width: 16, height: 16, borderWidth: 2 }} />
                    Analyzing photo...
                  </div>
                )}
                {faceValidation && !validatingFace && (
                  <div className={`p-3 rounded-xl border text-sm ${faceValidation.valid ? 'bg-green-500/10 border-green-500/30 text-green-300' : 'bg-red-500/10 border-red-500/30 text-red-300'}`} data-testid="face-validation-result">
                    {faceValidation.valid ? (
                      <div className="flex items-center gap-2">
                        <Check className="w-4 h-4" />
                        <span>Face detected — great photo for AI transformation!</span>
                      </div>
                    ) : (
                      <div>
                        {faceValidation.issues?.map((issue, i) => (
                          <p key={i} className="flex items-center gap-2 mb-1">
                            <AlertTriangle className="w-4 h-4 flex-shrink-0" />
                            {issue}
                          </p>
                        ))}
                        <button onClick={() => setShowGuidelines(true)} className="text-[#D4AF37] text-xs mt-2 hover:underline" data-testid="show-guidelines-btn">
                          View photo guidelines
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Enhanced Accuracy Toggle (Ultimate Pack only) */}
            {uploadedBase64 && faceValidation?.valid && (
              <div className="mt-4 p-4 glass-card" data-testid="enhanced-mode-section">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="flex items-center gap-2">
                      <Sparkles className="w-4 h-4 text-[#D4AF37]" />
                      <span className="text-sm font-medium">Enhanced Accuracy</span>
                      {!hasUltimate && <span className="text-[10px] bg-[#D4AF37] text-black px-1.5 py-0.5 rounded-full font-bold">ULTIMATE</span>}
                    </div>
                    <p className="text-xs text-white/40 mt-1">
                      {hasUltimate 
                        ? "Upload 2-3 extra photos for better face preservation"
                        : "Available with Ultimate Pack — upload extra photos for perfect accuracy"
                      }
                    </p>
                  </div>
                  <button
                    onClick={() => {
                      if (!hasUltimate) {
                        onNavigate("/pricing");
                        return;
                      }
                      setEnhancedMode(!enhancedMode);
                      if (enhancedMode) setAdditionalImages([]);
                    }}
                    className={`relative w-12 h-6 rounded-full transition-colors ${enhancedMode && hasUltimate ? 'bg-[#D4AF37]' : 'bg-white/20'}`}
                    data-testid="enhanced-toggle"
                  >
                    <div className={`absolute top-1 w-4 h-4 rounded-full bg-white transition-transform ${enhancedMode && hasUltimate ? 'translate-x-7' : 'translate-x-1'}`} />
                  </button>
                </div>
                
                {/* Additional Photo Uploads */}
                {enhancedMode && hasUltimate && (
                  <div className="mt-4">
                    <p className="text-xs text-white/50 mb-3">Add up to 3 more photos of yourself from different angles:</p>
                    <div className="grid grid-cols-3 gap-3">
                      {[0, 1, 2].map(idx => (
                        <div key={idx} className="relative">
                          {additionalImages[idx] ? (
                            <div className="relative">
                              <img src={additionalImages[idx].preview} alt={`Reference ${idx + 1}`} className="w-full h-24 object-cover rounded-lg" />
                              <button
                                onClick={() => {
                                  const newImgs = [...additionalImages];
                                  newImgs.splice(idx, 1);
                                  setAdditionalImages(newImgs);
                                }}
                                className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 rounded-full flex items-center justify-center text-white text-xs"
                              >
                                <X className="w-3 h-3" />
                              </button>
                            </div>
                          ) : (
                            <label className="flex flex-col items-center justify-center h-24 border border-dashed border-white/20 rounded-lg cursor-pointer hover:border-[#D4AF37]/50 transition-colors">
                              <Camera className="w-5 h-5 text-white/30 mb-1" />
                              <span className="text-[10px] text-white/30">Angle {idx + 1}</span>
                              <input
                                type="file"
                                accept="image/*"
                                className="hidden"
                                onChange={(e) => {
                                  const file = e.target.files[0];
                                  if (!file) return;
                                  const reader = new FileReader();
                                  reader.onload = (ev) => {
                                    const base64 = ev.target.result.split(",")[1];
                                    const newImgs = [...additionalImages];
                                    newImgs[idx] = { preview: ev.target.result, base64 };
                                    setAdditionalImages(newImgs);
                                  };
                                  reader.readAsDataURL(file);
                                }}
                                data-testid={`enhanced-upload-${idx}`}
                              />
                            </label>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Step 3: Generate */}
            {selectedStyle && uploadedImage && !generating && (
              <div className="max-w-xl mx-auto">
                <button 
                  onClick={() => { setUploadedImage(null); setUploadedBase64(null); }}
                  className="flex items-center gap-2 text-white/60 hover:text-white mb-8 transition-colors"
                  data-testid="change-photo"
                >
                  <ChevronLeft className="w-4 h-4" />
                  Change Photo
                </button>
                
                <div className="glass-card p-2 mb-8">
                  <img 
                    src={uploadedImage} 
                    alt="Uploaded" 
                    className="w-full aspect-[3/4] object-cover"
                    data-testid="uploaded-preview"
                  />
                </div>
                
                <div className="flex items-center justify-between mb-6 p-4 glass-card">
                  <div>
                    <p className="text-xs uppercase tracking-[0.2em] text-white/40">Selected Style</p>
                    <p className="font-serif text-xl capitalize">
                      {selectedStyle === "all" ? "All 12 Styles" : selectedStyle.replace("_", " ")}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-xs uppercase tracking-[0.2em] text-white/40">Cost</p>
                    <p className="font-serif text-xl">
                      {selectedStyle === "all" ? (
                        <><span className="text-[#D4AF37]">3</span> <span className="text-sm line-through text-white/40">5</span> Credits</>
                      ) : (
                        "1 Credit"
                      )}
                    </p>
                  </div>
                </div>
                
                <button 
                  onClick={handleGenerate}
                  className="btn-primary w-full flex items-center justify-center gap-2"
                  data-testid="generate-button"
                >
                  <Sparkles className="w-4 h-4" />
                  {selectedStyle === "all" ? "Generate All 12 Styles" : "Generate Portrait"}
                </button>
              </div>
            )}

            {/* Generating State */}
            {generating && (
              <div className="max-w-xl mx-auto">
                <PremiumLoader message={selectedStyle === "all" ? "Creating all 12 masterpieces..." : "Crafting your portrait..."} />
                {multiResults && multiResults.length > 0 && (
                  <p className="text-center text-gold text-sm mt-2">{multiResults.length}/12 styles complete</p>
                )}
              </div>
            )}
          </>
        )}
        
        {/* Credit Upsell Modal */}
        <CreditUpsellModal 
          isOpen={showUpsell}
          onClose={() => setShowUpsell(false)}
          onNavigate={onNavigate}
          creditsNeeded={selectedStyle === "all" ? 7 : 1}
          currentCredits={user?.credits || 0}
        />

        {/* Photo Guidelines Modal */}
        <AnimatePresence>
          {showGuidelines && (
            <motion.div
              className="fixed inset-0 z-[100] flex items-center justify-center"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            >
              <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={() => setShowGuidelines(false)} />
              <motion.div
                className="relative bg-[#0a0a0a] border border-white/10 rounded-2xl p-6 max-w-lg w-full mx-4 max-h-[80vh] overflow-y-auto"
                initial={{ scale: 0.9, y: 20 }}
                animate={{ scale: 1, y: 0 }}
                exit={{ scale: 0.9, y: 20 }}
                data-testid="guidelines-modal"
              >
                <div className="absolute top-0 left-0 right-0 h-1 bg-[#D4AF37]/50 rounded-t-2xl" />
                <div className="text-center mb-6">
                  <Camera className="w-10 h-10 text-[#D4AF37] mx-auto mb-3" />
                  <h3 className="font-serif text-2xl">Photo Guidelines</h3>
                  <p className="text-white/50 text-sm mt-1">Follow these tips for the best AI portrait results</p>
                </div>
                
                <div className="space-y-4">
                  <div className="flex gap-4">
                    <div className="flex-1 p-4 bg-green-500/10 border border-green-500/20 rounded-xl">
                      <p className="text-green-400 font-medium text-sm mb-2 flex items-center gap-1"><Check className="w-4 h-4" /> Do</p>
                      <ul className="text-xs text-white/60 space-y-1.5">
                        <li>Front-facing photo, eyes visible</li>
                        <li>Good, even lighting on face</li>
                        <li>Neutral or natural expression</li>
                        <li>Sharp, in-focus image</li>
                        <li>Face takes up 30%+ of image</li>
                        <li>High resolution (500px+ width)</li>
                      </ul>
                    </div>
                    <div className="flex-1 p-4 bg-red-500/10 border border-red-500/20 rounded-xl">
                      <p className="text-red-400 font-medium text-sm mb-2 flex items-center gap-1"><X className="w-4 h-4" /> Don't</p>
                      <ul className="text-xs text-white/60 space-y-1.5">
                        <li>Sunglasses or face coverings</li>
                        <li>Heavy shadows on face</li>
                        <li>Blurry or motion-blurred photos</li>
                        <li>Group photos (1 face only)</li>
                        <li>Extreme angles or tilts</li>
                        <li>Very dark or overexposed shots</li>
                      </ul>
                    </div>
                  </div>
                </div>

                <button
                  onClick={() => setShowGuidelines(false)}
                  className="btn-primary w-full mt-6"
                  data-testid="close-guidelines-btn"
                >
                  Got It
                </button>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </motion.div>
  );
};


